import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import MilestoneEditor from '@/components/Milestones/MilestoneEditor';
import { Query } from '@/utils/react-query/Queries';
import { MILESTONES_ENABLED, MILESTONE_SCOPES } from '@/config/milestones';
import { canCreateMilestones } from '@/utils/milestonePermissions';
import useMilestoneMutations from '@/utils/hooks/useMilestoneMutations';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';

/**
 * Create flow: choose scope → metric → filters → thresholds → copy →
 * badge → audience, all in one scroll. scope can be preselected via
 * /milestones/create?scope=INDIVIDUAL.
 */
export default function CreateMilestoneScreen() {
    const router = useRouter();
    const { scope: scopeParam } = useLocalSearchParams();
    const { data: loggedInUser, isLoading: userLoading } = Query.people.loggedInUser();
    const { data: catalog, isLoading: catalogLoading } = Query.milestones.catalog();
    const { save } = useMilestoneMutations({ organizationID: loggedInUser?.organizationID });

    if (userLoading || catalogLoading) {
        return <View style={styles.center}><ActivityIndicator /></View>;
    }

    if (!MILESTONES_ENABLED || !canCreateMilestones(loggedInUser)) {
        return (
            <View style={styles.center} testID="milestone-create-requires-permission">
                <EmptyListPlaceholder noLottie spacing={48} message="You don't have permission to create milestones." />
            </View>
        );
    }

    const initialScope = scopeParam === MILESTONE_SCOPES.INDIVIDUAL ? MILESTONE_SCOPES.INDIVIDUAL : MILESTONE_SCOPES.ORGANIZATION;

    const handleSubmit = (payload) => {
        save.mutate({ definition: payload, expectedRevision: null }, {
            onSuccess: (saved) => {
                if (saved?.id) {
                    router.replace(`/settings/organization/milestones/${saved.id}`);
                } else {
                    router.back();
                }
            },
            onError: (error) => {
                if (error?.milestoneCode !== 'MILESTONE_REVISION_CONFLICT') return;
                Toast.show({ type: 'error', text2: 'This milestone changed while you were editing. Refresh and try again.' });
            },
        });
    };

    return (
        <View style={styles.container} testID="milestone-create-screen">
            <MilestoneEditor
                catalog={catalog}
                initialScope={initialScope}
                loggedInUser={loggedInUser}
                saving={save.isPending}
                onSubmit={handleSubmit}
                onCancel={() => router.back()}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
    },
}));
